import type { IsoDate, IsoDateTime, Uuid } from '@/shared/types/domain'

/**
 * El vocabulario del portal, que **no es el interno recortado**: es otro.
 *
 * Los tipos de acá son el reflejo exacto de los `schemas_client.py` de cada módulo del backend,
 * donde los campos están enumerados a mano. Por eso no extienden de
 * `ProjectSummary` ni de `Task`: si mañana el interno agrega un campo de notas
 * internas, acá no aparece solo, y un componente del portal no puede leer un
 * campo que su tipo no tiene.
 *
 * Lo que NO está tiene tanto peso como lo que está: **no hay nada del orden
 * interno** —ni el rank ni el ordinal 1..X— porque en qué orden el equipo
 * atiende a sus clientes no es asunto de un cliente. Tampoco hay responsable,
 * ni semáforo de salud, ni repos, ni accesos. Hay un test que lee estos
 * archivos y falla si alguna de esas palabras aparece.
 */

/** `GET /portal/me`. Ni rol ni lista de empresas: el cliente tiene una sola. */
export interface PortalMe {
  id: Uuid
  display_name: string
  email: string
  company_id: Uuid
  company_name: string
}

export interface PortalCompany {
  id: Uuid
  name: string
}

/** Contacto de la empresa, sin notas internas. */
export interface PortalContact {
  id: Uuid
  name: string
  email: string
  role_title: string | null
}

/** Ítem del stack marcado como visible. Solo llegan los que lo están. */
export interface PortalTechStackItem {
  category: string
  name: string
  version: string | null
  url: string | null
}

export interface PortalCompanyOverview {
  company: PortalCompany
  contacts: PortalContact[]
  tech_stack: PortalTechStackItem[]
}

/**
 * Tarjeta de `/portal`.
 *
 * `stage_label` llega **ya traducido** del backend (`En pausa`, `Por iniciar`,
 * `En desarrollo`, `En revisión con ustedes`, `Entregado`). No se vuelve a
 * mapear acá: la traducción se hace por categoría y del lado del server, y
 * tener el mismo diccionario dos veces es garantizar que algún día discrepen.
 */
export interface PortalProjectListItem {
  id: Uuid
  name: string
  stage_label: string
  /** La fase como **dato**, no como texto. Ver `PhaseTone`. */
  stage_tone: PhaseTone
  is_ongoing: boolean
  target_end_date: IsoDate | null
  /** Título de la próxima tarea VISIBLE sin completar. `null` es una respuesta. */
  next_milestone: string | null
}

export interface PortalProject {
  id: Uuid
  name: string
  /** `client_summary_md`, que es un campo aparte del brief interno. */
  summary_md: string | null
  stage_label: string
  stage_tone: PhaseTone
  is_ongoing: boolean
  start_date: IsoDate | null
  target_end_date: IsoDate | null
  open_tasks: number
  completed_tasks: number
}

/** Tarea visible: qué y para cuándo. Sin responsable, sin estimación, sin notas. */
export interface PortalTask {
  id: Uuid
  title: string
  stage_label: string
  stage_tone: PhaseTone
  due_date: IsoDate | null
  is_completed: boolean
}

export interface PortalDocumentListItem {
  id: Uuid
  title: string
  doc_type: string
  kind: 'markdown' | 'link'
  external_url: string | null
  updated_at: IsoDateTime
}

export interface PortalDocument extends PortalDocumentListItem {
  content_md: string | null
}

export interface PortalProjectDetail {
  project: PortalProject
  tasks: PortalTask[]
  documents: PortalDocumentListItem[]
}

/** Nodo del diagrama del portal: sin repo, sin acceso, sin hosting, sin URL. */
export interface PortalComponent {
  id: Uuid
  key: string
  name: string
  kind: string
  tech: string | null
  position_x: number | null
  position_y: number | null
}

export interface PortalComponentLink {
  from_component_id: Uuid
  to_component_id: Uuid
  label: string | null
  protocol: string
  is_async: boolean
}

export interface PortalArchitecture {
  components: PortalComponent[]
  links: PortalComponentLink[]
}

export interface PortalBoardColumn {
  stage: { label: string; color_token: string | null }
  projects: PortalProjectListItem[]
}

export interface PortalBoard {
  columns: PortalBoardColumn[]
  ongoing: PortalProjectListItem[]
}

/**
 * La fase como **dato**, no como texto: `stage_tone`.
 *
 * Es la categoría **de cliente** que manda el backend
 * (`portal.service.fase_visible`): no depende del idioma y ya trae colapsado lo
 * que el portal decide no contar — `blocked` y `paused` salen los dos como
 * `paused`, porque al cliente no se le dice que su proyecto está trabado.
 *
 * Antes esto se deducía comparando `stage_label` contra una lista de cadenas
 * **en español**. Funcionaba para un cliente hispanohablante y sólo para él: en
 * `pt` y en `en` ninguna entrada matcheaba, así que el chip de fase caía a gris
 * y la barra de avance directamente no se dibujaba. El estado pasaba de
 * comunicarse con color + ícono + texto —la regla transversal del diseño— a
 * comunicarse sólo con texto, y la tarjeta perdía lo único que contesta
 * "¿cuánto falta?". El QA funcional lo levantó como 5.1 y era el hallazgo de
 * i18n que más se ve desde afuera, porque es lo que mira el cliente.
 *
 * El tono ya viajaba para el proyecto; faltaba para la tarea y para la columna
 * del tablero, y ningún componente lo usaba. Era, otra vez, un dato calculado
 * de un lado y no consumido del otro.
 */
export type PhaseTone = 'not_started' | 'in_progress' | 'review' | 'paused' | 'done'

/**
 * El camino que recorre un proyecto, en orden.
 *
 * `paused` no está: no es una etapa del camino, es una excepción que lo
 * detiene. Se trata aparte (ver `PAUSED_TONE`).
 */
export const CLIENT_PHASES: readonly PhaseTone[] = ['not_started', 'in_progress', 'review', 'done']

/** La pausa, que detiene el camino en vez de avanzarlo. */
export const PAUSED_TONE: PhaseTone = 'paused'

export interface PhaseProgress {
  step: number
  total: number
}

export function phaseProgress(tone: PhaseTone): PhaseProgress | null {
  const index = CLIENT_PHASES.indexOf(tone)
  if (index === -1) return null
  return { step: index + 1, total: CLIENT_PHASES.length }
}

/**
 * El tipo de documento es un enum del backend, o sea vocabulario del sistema:
 * se traduce. El mapa devuelve **la clave del catálogo**, no el texto, para que
 * quien dibuja lo resuelva con el idioma de quien está mirando.
 */
export const DOC_TYPE_KEY: Record<string, string> = {
  brief: 'portal.docType.brief',
  spec: 'portal.docType.spec',
  design: 'portal.docType.design',
  runbook: 'portal.docType.runbook',
  onboarding: 'portal.docType.onboarding',
  note: 'portal.docType.note',
  other: 'portal.docType.other',
}

/**
 * `null` para un tipo que el catálogo no conoce: ahí se muestra el valor crudo,
 * que es más honesto que inventarle un nombre o que dibujar la clave.
 */
export function docTypeKey(docType: string): string | null {
  return DOC_TYPE_KEY[docType] ?? null
}
